import React, { useState, useEffect, useContext, useRef } from "react";
import { LGrid, LButton, FileMapperMultiStepMain } from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faPlus, faTrash, faEdit, faCopy, faFileArrowUp, faSpinner} from "@fortawesome/free-solid-svg-icons";
import styles from "../TemplateManagement.module.css";
import { DeleteConfirmation } from "../TemplateManagementModals/SharedModals/DeleteConfirmationModal";
import { AuthContext } from "../../../../../../context/AuthContext";
import SelectClientModal from "../TemplateManagementModals/ClientTemplateMapModals/SelectClientModal.jsx";
import EditTemplateMapModal from "../TemplateManagementModals/ClientTemplateMapModals/EditTemplateMapModal";
import { createPortal } from "react-dom";
const GATEWAY_BASE_URL = process.env.REACT_APP_API_GATEWAY_BASE;

function ClientTemplates({ loffaTemplates, requestUrls }) {
  const [data, setData] = useState();
  const [itemToEdit, setItemToEdit] = useState(null);
  const [itemToDuplicate, setItemToDuplicate] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showClientSelectModal, setShowClientSelectModal] = useState(null);
  const [showFileMapper, setShowFileMapper] = useState(false);
  const [activeClientList, setActiveClientList] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTemplateMap, setSelectedTemplateMap] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false)
  const gridRef = useRef(null);
  const authContext = useContext(AuthContext);
  const token = authContext.authState().accessToken.accessToken
  useEffect(() => {
    //fetches clients & filters out inactive
    const fetchClientList = async () => {
      setLoading(true);
      try {
        const clientListUrl = GATEWAY_BASE_URL + "/multitenancy/api/Clients/Clients";
        const response = await fetch(clientListUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) throw new Error("Failed to fetch Template Maps.");
        const result = await response.json();
        //custom map to format client list
        const clients = result?.filter(c => c.isActive).map((e) => {
          return {
            formattedName: e.formattedName,
            clientValuePayload: {
              dtcNumber: e.dtcNumber,
              clientGuid: e.clientGuid,
            },
          };
        });
        setActiveClientList(clients);
        setError(null);
      } catch (error) {
        setError("Error fetching Client list: " + error.message);
      } finally {
        setLoading(false)
      }
    };

    fetchClientList();
  }, [authContext, token]);

  const fetchAllTemplateMapData = async () => {
    try {
    setLoading(true);
    const dataManagerReqUrl = `${GATEWAY_BASE_URL}/datamanager/api/Template/TemplateMap/`;
    const productDataUrl = `${GATEWAY_BASE_URL}/multitenancy/api/ProductType/`;
    const [templateMapResponse, productTypeResponse] = await Promise.all([
      fetch(dataManagerReqUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      fetch(productDataUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    ]);

    if (!templateMapResponse.ok) throw new Error("Failed to fetch Template Maps.");
    if (!productTypeResponse.ok) throw new Error("Failed to fetch ProductTypes.");

    const [templateMapData, productDetailResult] = await Promise.all([
      templateMapResponse.json(),
      productTypeResponse.json(),
    ]);

    const { productTypes } = productDetailResult;

    //a custom mapper to include both the client's formatted name & product name to display in table
    const combined = templateMapData.map((item) => {
        const productType = productTypes?.find((p) => p.id === item.clientProductId);
        const client = activeClientList?.find((e)=> e.clientValuePayload.clientGuid === item.clientId)
        const loffaTemplateName = loffaTemplates?.find((e)=> e.templateId === item.loffaTemplateId)
        return {
          ...item,
          productName: productType ? productType?.name : item.clientProductId,
          client: client?.formattedName,
          loffaTemplateName: loffaTemplateName?.name
        };
      });
      setData(combined);
      setError(null);
    } catch (error) {
      setError("Error loading data: " + error.message);
    } finally{
      setLoading(false)
    }
  };

  const fetchTemplateMapById = async (templateMapId) => {
    try {
      const dataManagerReqUrl = GATEWAY_BASE_URL + "/datamanager/api/Template/TemplateMap/" + templateMapId + "/";
      const response = await fetch(dataManagerReqUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch Template Maps.");
      const result = await response.json();
      const client = activeClientList?.find((e)=> e.clientValuePayload.clientGuid === result.clientId)
      setSelectedClient(client)
      setSelectedTemplateMap(result);
      setShowFileMapper(true)
      setError(null);
    } catch (error) {
      setError("Error loading data: " + error.message);
    }
  };

  useEffect(() => {
    if (Array.isArray(activeClientList) && activeClientList.length && Array.isArray(loffaTemplates) && loffaTemplates.length) {
      fetchAllTemplateMapData();
    }
  }, [authContext, activeClientList, loffaTemplates, token]);

  //handles selecting a client to create a template for
  const onClientSelect = (e) => {
    setSelectedClient(e.target.value);
  };

  //handles opening the file mapper after admin selects client to create map for
  const onClientSelectConfirm = () => {
    setShowClientSelectModal(false);
    setShowFileMapper(true)
  };

  const onClientSelectClose = () => {
    setSelectedClient(null);
    setShowClientSelectModal(false);
  };

  //called when the FileMapper ui module is closed
  const handleTemplateMapperClose = async () => {
    await fetchAllTemplateMapData() //refetches maps to render a newly created map when closed
    setShowFileMapper(false)
    setSelectedClient(null);
    setSelectedTemplateMap(null)
  }

  const handleDelete = async (templateMapId) => {
    try {
      const response = await fetch(
        `${GATEWAY_BASE_URL}/datamanager/api/Template/TemplateMap/${templateMapId}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to delete template map.");

      setData((prev) =>
        prev.filter((item) => item.templateMapId !== templateMapId)
      );
      setError(null);
      setItemToDelete(null);
      await fetchAllTemplateMapData()
    } catch (error) {
      setError("Error deleting template: " + error.message);
      setItemToDelete(null);
    }
  };

  
  const columns =  [
    {
      field: "actions",
      title: "Actions",
      width: 125,
      cell: (props) => (
        <td className={styles.templateManagerActionButtons}>
          <button //Allows admins to apply a clients map to a file
            className={styles.templateManagerUseTemplateButton}
            onClick={() => fetchTemplateMapById(props.dataItem.templateMapId)}
            title="Use Template Map"
          >
            <FontAwesomeIcon icon={faFileArrowUp} />
          </button>
          <button
            className={styles.templateManagerEditButton}
            onClick={() => setItemToEdit(props.dataItem)}
            title="Edit"
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button
            className={styles.templateManagerDuplicateButton}
            onClick={() => setItemToDuplicate(props.dataItem)}
            title="Duplicate"
          >
            <FontAwesomeIcon icon={faCopy} />
          </button>
          <button
            className={styles.templateManagerDeleteButton}
            onClick={() => setItemToDelete(props.dataItem)}
            title="Delete"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </td>
      ),
      filterable: false,
    },
    { field: "name", title: "Mapper Name", width: 250 },
    { field: "productName", title: "Product", width: 250 },
    { field: "loffaTemplateName", title: "Loffa Template", width: 225 },
    { field: "client", title: "Client", width: 225 },
  ];

  //Loading UI for loading state while templates are being fetched
const loadingPanelMarkup = (
    <div className={styles.templateManagerGridLoadSpinner}>
        <FontAwesomeIcon icon = {faSpinner} spin/>
    </div>
);
const LoadingPanel = (props) => {
  const { gridRef } = props;
  const gridContent = gridRef.current && gridRef.current.querySelector('.k-grid-content');
  return gridContent ? createPortal(loadingPanelMarkup, gridContent) : loadingPanelMarkup;
};

  return (
    <div className={styles.templateManagerContainer}>
      <div className={styles.templateManagerHeader}>
        <h2>Client Template Maps</h2>
        
        <LButton onClick={() => setShowClientSelectModal(true)} themeColor="primary"
              className="green">
          <span className={styles.templateManagerAddButton}>
            <FontAwesomeIcon
              icon={faPlus}
              className={styles.templateManagerButtonIcon}
            />
            <span>Create File Map for Client</span>
          </span>
        </LButton>
      </div>

      {error && <div className={styles.templateManagerError}>{error}</div>}

      <div ref={gridRef}>
        {loading && <LoadingPanel gridRef={gridRef} />}
        <LGrid
          resizable={true}
          data={loading ? [{ id: 'loading-dummy' }] : data}
          columns={columns}
          sortable
          filterable
          pageable
          style={{ minHeight: "400px" }}
          className={`${styles.templateManagerGrid} ${
            loading ? styles.gridLoadingState : ''
          }`}
        />
      </div>

      {/* operation modals */}
      <DeleteConfirmation
        isOpen={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        onConfirm={() => handleDelete(itemToDelete?.templateMapId)}
        title="Delete Client Template"
        message={`Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`}
      />

       {itemToEdit && (
        <EditTemplateMapModal
          mode="update"
          isOpen={!!itemToEdit}
          initialData={itemToEdit}
          onClose={() => setItemToEdit(null)}
          onRefetch={fetchAllTemplateMapData}
          clients={activeClientList}
          loffaTemplates={loffaTemplates}
        />
      )}
      {itemToDuplicate && (
        <EditTemplateMapModal
          mode="duplicate"
          isOpen={!!itemToDuplicate}
          initialData={itemToDuplicate}
          onClose={() => setItemToDuplicate(null)}
          onRefetch={fetchAllTemplateMapData}
          clients={activeClientList}
          loffaTemplates={loffaTemplates}
        />
      )}

      <SelectClientModal
        selectedClient={selectedClient}
        showClientSelect={showClientSelectModal}
        clients={activeClientList}
        onClientSelect={onClientSelect}
        onClientSelectClose={onClientSelectClose}
        onClientSelectConfirm={onClientSelectConfirm}
      />

      {/* File Mapper ui module - where file maps are created */}
      {showFileMapper ? (
        <FileMapperMultiStepMain
          accessToken={authContext.authState().accessToken.accessToken}
          onClose={handleTemplateMapperClose}
          isCreatingFromAdminPortal={true}
          requestUrls={requestUrls}
          adminSelectedTemplateMap={selectedTemplateMap}
          adminSelectedClient={selectedClient}
        />
      ) : null}
    </div>
  );
}

export { ClientTemplates };
